import { RoomStatus } from '../interfaces/room.interface';

// ============================================
// Player session — identity that survives reconnects
// ============================================
//
// A player is identified by a stable public `playerId`, resolved from a
// secret session token during the socket.io handshake. The socket id is only
// the current transport and changes on every reconnect; it is never used as
// identity. Only one socket may be live per session at a time.

/**
 * Sent by the client as socket.io handshake `auth` on every (re)connection.
 */
export interface SessionHandshakeAuth {
  /** Session token from a previous WELCOME. Omitted on a first visit. */
  token?: string;
  /** Display name to register for this session. */
  username?: string;
  /**
   * Take the session over from another live socket (another tab). Without it,
   * a handshake for a session that is already live is rejected.
   */
  takeover?: boolean;
}

/** Why the server refused a handshake (`connect_error` → `err.data.reason`). */
export enum SessionRejectReason {
  /** The session already has a live socket — another tab is playing. */
  ALREADY_CONNECTED = 'already_connected',
  /** The `auth` payload was malformed. */
  INVALID_HANDSHAKE = 'invalid_handshake',
}

/** `data` attached to a rejected handshake's `connect_error`. */
export interface SessionConnectErrorData {
  reason: SessionRejectReason;
}

/** The room a session is currently a member of. */
export interface ActiveRoomDto {
  roomId: string;
  joinCode: string;
  roomName: string;
  status: RoomStatus;
  /**
   * Present while the member is away from the room page: how long until they
   * are removed from the room (ms, relative, so client clock skew does not matter).
   */
  awayExpiresInMs?: number;
}

/**
 * First event on every identified connection.
 */
export interface WelcomeDto {
  /** Stable public identity. Every `playerId` / `hostId` in room and game state uses it. */
  playerId: string;
  /** Present only when the server issued a new token (the client sent none). */
  issuedToken?: string;
  username: string | null;
  /** The room this session belongs to, if any — e.g. to resume after a takeover. */
  activeRoom?: ActiveRoomDto;
  /** True when this connection took the session over from another socket. */
  tookOver: boolean;
  /**
   * The server's clock (ms) when it sent this. Every timestamp the server
   * sends is on its clock; the client converts them to its own with this.
   */
  serverTime: number;
}

/**
 * Sent to a socket right before the server disconnects it because another
 * connection took its session over. The client must not auto-reconnect.
 */
export interface SessionReplacedDto {
  reason: 'taken_over';
}
